import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  FriendlyError,
  RadioCard,
  SectionLoading,
  StepHeading,
  StepShell,
} from "@codel/dova-ds";
import { Button } from "../onboarding/components/Button";
import { FlowMessageScreen, FlowOutcomeScreen, FlowSavedScreen } from "../flows/FlowOutcomeScreen";
import { useFlowBack } from "../flows/useFlowBack";
import {
  acceptRescheduleOfferMeRescheduleOffersOfferUuidAcceptPost,
  getRescheduleOfferMeRescheduleOffersOfferUuidGet,
} from "@/__generated__/api/me/me";
import type { RescheduleCandidateOut } from "@/__generated__/api/wrenAPI.schemas";
import { ApiError } from "@/api/client";
import { useMeContext } from "../MeApp";
import { useInvalidateSessions } from "../hooks/sessionHooks";
import { RescheduleSlotContent } from "./ReschedulePrimitives";
import { FALLBACK_THERAPIST_NAME, PICK_PAGE_COPY } from "./rescheduleCopy";
import {
  classifyRescheduleError,
  type RescheduleFailure,
  unexpectedErrorDetail,
} from "./rescheduleOutcome";
import { COUPLE_EVENTS } from "../analytics/events";
import { track } from "../analytics/track";

interface PickConflictBody {
  detail?: {
    code?: string;
    remaining_slots?: RescheduleCandidateOut[];
  } | string;
}

export function ReschedulePickPage() {
  const { offerUuid = "" } = useParams();
  const navigate = useNavigate();
  // Back returns to wherever the offer was opened from; from a text-message link, Home.
  const goBack = useFlowBack();
  const goHome = () => navigate("/me/home");
  const { couple } = useMeContext();
  const invalidateSessions = useInvalidateSessions();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [remainingSlots, setRemainingSlots] = useState<RescheduleCandidateOut[] | null>(null);
  const [raceMessage, setRaceMessage] = useState<string | null>(null);
  const [submitFailure, setSubmitFailure] = useState<RescheduleFailure | null>(null);
  const [needsReoffer, setNeedsReoffer] = useState(false);
  const [acceptedSlot, setAcceptedSlot] = useState<RescheduleCandidateOut | null>(null);

  const offer = useQuery({
    queryKey: ["me", "reschedule-offers", offerUuid],
    enabled: !!offerUuid,
    retry: false,
    queryFn: async () => {
      const response = await getRescheduleOfferMeRescheduleOffersOfferUuidGet(offerUuid);
      if (response.status !== 200) {
        // Unreachable at runtime — the generated client throws `ApiError` for any non-2xx before
        // we get here. Retained only to narrow the response union to the success payload. Unlike
        // the offer flow's equivalent this is a BARE `Error`, carrying no code, so if it ever were
        // reached `classifyRescheduleError` would find nothing to map and fall through to the
        // unexpected path — which is the correct answer for a state that cannot happen.
        throw new Error("This reschedule offer could not be loaded");
      }
      return response.data;
    },
    refetchInterval: (query) => query.state.data?.status === "releasing" ? 1500 : false,
  });

  const visibleSlots = remainingSlots ?? offer.data?.slots ?? [];
  const acceptOffer = useMutation({
    mutationFn: async (candidateId: string) => {
      if (!offer.data) {
        throw new Error("Offer choices are not ready");
      }
      const response = await acceptRescheduleOfferMeRescheduleOffersOfferUuidAcceptPost(offerUuid, {
        candidate_id: candidateId,
        expected_version: offer.data.version,
      });
      return response.data;
    },
    onSuccess: (_result, candidateId) => {
      if (offer.data) {
        track(COUPLE_EVENTS.rescheduleAccepted, {
          offer_id: offerUuid,
          session_id: offer.data.session_uuid,
        });
      }
      const picked = visibleSlots.find((slot) => slot.candidate_id === candidateId);
      if (picked) {
        setAcceptedSlot(picked);
      }
      invalidateSessions();
    },
    onError: (error) => {
      setSelectedId(null);
      if (error instanceof ApiError && error.status === 409) {
        const body = error.data as PickConflictBody;
        const detail = typeof body.detail === "object" ? body.detail : null;
        if (detail?.code === "reschedule_slot_unavailable") {
          const remaining = detail.remaining_slots ?? [];
          if (remaining.length > 0) {
            setRemainingSlots(remaining);
            setRaceMessage(PICK_PAGE_COPY.raceMessage);
            return;
          }
          setNeedsReoffer(true);
          return;
        }
      }
      setSubmitFailure(classifyRescheduleError(error, PICK_PAGE_COPY.acceptFailed));
    },
  });

  // Same rule as the offer flow: a KNOWN reschedule code is a business answer, so it gets an
  // explanatory screen in every environment rather than `FriendlyError`'s "something went wrong"
  // (whose detail production drops entirely). Anything unmapped stays on the FriendlyError path.
  const failure = classifyRescheduleError(offer.error) ?? submitFailure;

  if (acceptedSlot) {
    return (
      <FlowSavedScreen
        title={PICK_PAGE_COPY.accepted.title}
        body={PICK_PAGE_COPY.accepted.body}
        onHome={goHome}
      >
        <div className="mt-8 flex flex-col gap-4 text-left">
          <Card className="p-6">
            <div className="dova-eyebrow mb-2">{PICK_PAGE_COPY.accepted.newSessionEyebrow}</div>
            <RescheduleSlotContent slot={acceptedSlot} />
          </Card>
          <p className="m-0 text-center [font:var(--type-caption)] text-secondary">{PICK_PAGE_COPY.accepted.invitesSent}</p>
        </div>
      </FlowSavedScreen>
    );
  }

  if (failure?.kind === "expected") {
    return (
      <FlowOutcomeScreen
        outcome={failure.outcome}
        therapistName={couple?.therapistName ?? FALLBACK_THERAPIST_NAME}
        onBack={goHome}
      />
    );
  }

  const showNeedsReoffer = needsReoffer || offer.data?.status === "needs_reoffer";
  if (showNeedsReoffer) {
    return (
      <FlowMessageScreen
        title={PICK_PAGE_COPY.needsReoffer.title}
        body={PICK_PAGE_COPY.needsReoffer.sub}
        note={PICK_PAGE_COPY.needsReoffer.body}
        onAction={goHome}
      />
    );
  }

  if (offer.data && offer.data.status !== "awaiting_pick") {
    const preparing = offer.data.status === "releasing";
    return (
      <FlowMessageScreen
        title={preparing ? PICK_PAGE_COPY.preparing.title : PICK_PAGE_COPY.resolved.title}
        body={preparing ? PICK_PAGE_COPY.preparing.sub : PICK_PAGE_COPY.resolved.sub}
        onAction={goHome}
      >
        {preparing ? <SectionLoading label={PICK_PAGE_COPY.preparing.loadingLabel} /> : null}
      </FlowMessageScreen>
    );
  }

  return (
    <StepShell
      showBack
      onBack={goBack}
      footer={
        <Button
          disabled={!selectedId || acceptOffer.isPending}
          onClick={() => {
            if (selectedId) {
              acceptOffer.mutate(selectedId);
            }
          }}
        >
          {PICK_PAGE_COPY.acceptLabel}
        </Button>
      }
    >
      <StepHeading title={PICK_PAGE_COPY.title}>
        <p className="m-0">{PICK_PAGE_COPY.sub}</p>
      </StepHeading>

      {offer.isLoading ? <SectionLoading label={PICK_PAGE_COPY.loadingLabel} /> : null}
      <FriendlyError detail={unexpectedErrorDetail(failure)} />
      {raceMessage ? (
        <Card tone="soft" className="border-danger-bd bg-danger-bg p-4 [font:var(--type-caption)] text-danger">
          {raceMessage}
        </Card>
      ) : null}

      {offer.data ? (
        <div className="flex flex-col gap-2">
          {visibleSlots.map((slot) => (
            <RadioCard
              key={slot.candidate_id}
              name="reschedule-time"
              value={slot.candidate_id}
              checked={selectedId === slot.candidate_id}
              onChange={(candidateId) => {
                setSelectedId(candidateId);
                setRaceMessage(null);
                setSubmitFailure(null);
              }}
              label={<RescheduleSlotContent slot={slot} />}
            />
          ))}
        </div>
      ) : null}
    </StepShell>
  );
}
